'use client'
import { useSearchType } from '@/api/hooks'
import Grid from '@/components/grid/grid'
import VideoGrid from '@/components/grid/video-grid'
import { SearchTypeContext } from '@/context/search-type'
import UAParserContext from '@/context/ua-parser'
import { cn } from '@/util/cn'
import { useClientSideAPIKey } from '@/util/url'
import { GiphyFetch, MediaType, GifsResult } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { useContext } from 'react'
import Loader from '../components/loader'
import { useIsMobile } from '../util/use-media-query'
import { fetchType, createFetchSearchType, fetchTrendingText } from './mobile-content-utils'
import { useGiphyFetch } from '../util/use-gf'

type Props = {
    trendingGifs?: IGif[]
    children?: React.ReactNode
    className?: string
    columnOffsets?: number[]
    hasClickableAd?: boolean
}

const fetchConfig = {
    clientMobile: 12,
    clientDesktop: 12,
} as const

const getTrackingUrl = (page: number) => `/homepage/page/${page}`

const createFetchMobileContentType =
    (gf: GiphyFetch, fetchSearchType: ReturnType<typeof createFetchSearchType>) =>
    async (type: string, offset: number, limit: number): Promise<GifsResult> => {
        switch (type) {
            case 'emoji':
            case 'memes':
            case 'reactions':
                return await fetchType(type, offset, limit)
            case 'animals':
                return await fetchSearchType('animals', offset, limit, type)
            case 'text':
                return await fetchTrendingText(gf, offset, limit)
            default:
                throw new Error(`Unsupported mobile content type: ${type}`)
        }
    }

const HomeGrid = ({ trendingGifs = [], columnOffsets, className, hasClickableAd }: Props) => {
    const { deviceType } = useContext(UAParserContext)
    const apiKey = useClientSideAPIKey()
    const clientGiphyFetch = new GiphyFetch(apiKey)
    const isMobile = useIsMobile()
    const { isTransitioning } = useContext(SearchTypeContext)
    const type = useSearchType()

    const isMobileOnlyType = ['emoji', 'memes', 'reactions', 'animals', 'text'].includes(type)
    const gf = useGiphyFetch()
    const fetchSearchType = createFetchSearchType(gf)
    const fetchMobileContentType = createFetchMobileContentType(gf, fetchSearchType)

    const trending = (type?: MediaType) => async (offset: number) =>
        clientGiphyFetch.trending({
            offset,
            type,
            limit: deviceType === 'desktop' ? fetchConfig.clientDesktop : fetchConfig.clientMobile,
        })

    const createGridFetch = () => {
        if (isMobileOnlyType) {
            return async (offset: number) => {
                const result = await fetchMobileContentType(
                    type,
                    offset,
                    deviceType === 'desktop' ? fetchConfig.clientDesktop : fetchConfig.clientMobile
                )
                return result
            }
        } else {
            return trending(type === 'clips' ? 'videos' : (type as MediaType))
        }
    }

    const gridFetch = createGridFetch()

    return (
        <div className={cn('relative mt-[6px] md:mt-0', className)}>
            {isTransitioning ? (
                <Loader />
            ) : type === 'clips' ? (
                <VideoGrid fetchGifs={gridFetch} gaTrackingUrl={getTrackingUrl} />
            ) : (
                <Grid
                    key={type}
                    initialGifs={trendingGifs}
                    fetchGifs={gridFetch}
                    columnOffsets={type === 'gifs' && !hasClickableAd && isMobile ? undefined : columnOffsets}
                    gaTrackingUrl={getTrackingUrl}
                />
            )}
        </div>
    )
}

export default HomeGrid
